// src/services/orderService.js
import apiClient from '../api/apiClient.js';
import { API_ENDPOINTS } from '../api/apiEndpoints.js';
import { getErrorMessage } from '../utils/apiError';

/**
 * Order Service - Handles all order-related API calls
 * Based on .NET ECommerceServer OrdersController endpoints
 */
export const orderService = {
  /**
   * Get all orders (Admin)
   * GET /Orders?CurrentPage&ItemsPerPage&State&SearchTerm&DateStart&DateEnd
   * filters: { state: 'PaymentSubmitted' | 'Paid,Fulfilled', searchTerm, dateStart, dateEnd (Date or ISO; end is inclusive) }
   */
  getAllOrders: async (currentPage = 1, pageSize = 20, filters = {}) => {
    try {
      const params = { CurrentPage: currentPage, ItemsPerPage: pageSize };
      if (filters.state) params.State = filters.state;
      if (filters.searchTerm?.trim()) params.SearchTerm = filters.searchTerm.trim();
      if (filters.dateStart) params.DateStart = new Date(filters.dateStart).toISOString();
      if (filters.dateEnd) params.DateEnd = new Date(filters.dateEnd).toISOString();
      const response = await apiClient.get(API_ENDPOINTS.ORDERS.GET_ALL, { params });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error, 'Failed to get orders'),
        statusCode: error.response?.status || 500,
      };
    }
  },

  /**
   * Create a new order
   * POST /api/orders
   */
  createOrder: async orderData => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.ORDERS.CREATE_DIGITAL,
        {
          productId: orderData.productId,
          paymentMethod: orderData.paymentMethod,
          orderNotes: orderData.orderNotes,
          // Required by the server: consent to licence terms + no-refund policy, and buyer state for the GST invoice
          acceptedTerms: orderData.acceptedTerms === true,
          buyerStateCode: orderData.buyerStateCode || null,
          buyerGstin: orderData.buyerGstin || null,
          buyerBusinessName: orderData.buyerBusinessName || null,
        }
      );

      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error, 'Failed to create order'),
        statusCode: error.response?.status || 500,
      };
    }
  },

  /**
   * Get order by database ID (the id in /profile/order-details/:orderId and order emails)
   * GET /Orders/{id} -> ApiResponse<Order>
   */
  getOrderById: async orderId => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.ORDERS.GET_BY_ID(orderId)
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error, "We couldn't load this order."),
        statusCode: error.response?.status || 500,
      };
    }
  },

  /**
   * Get order by order number (e.g. "PK-…")
   * GET /Orders/order/{orderId} -> ApiResponse<Order>
   */
  getOrderByOrderId: async orderId => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.ORDERS.GET_BY_ORDER_ID(orderId)
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error, "We couldn't load this order."),
        statusCode: error.response?.status || 500,
      };
    }
  },

  /**
   * The signed-in customer's orders, newest first.
   * GET /Orders/my-orders -> ApiResponse<{ items, currentPage, totalPages, pageSize, totalItems }>
   * dateStart / dateEnd are ISO date-times (inclusive).
   */
  getMyOrders: async ({ currentPage = 1, itemsPerPage = 10, dateStart, dateEnd } = {}) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ORDERS.GET_MY_ORDERS, {
        params: {
          currentPage,
          itemsPerPage,
          dateStart: dateStart || undefined,
          dateEnd: dateEnd || undefined,
          sortBy: 'OrderDate',
          sortOrder: 'desc',
        },
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error, "We couldn't load your orders."),
        statusCode: error.response?.status || 500,
      };
    }
  },

  getOrderPaymentStatus: async orderId => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.ORDERS.GET_PAYMENT_STATUS(orderId)
      );
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Get order payment status error:', error);
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to get order payment status',
        statusCode: error.response?.status || 500,
      };
    }
  },

  /**
   * Update order status
   * PUT /Orders/update-status
   */
  updateOrderStatus: async (updateData) => {
    try {
      const response = await apiClient.put(
        API_ENDPOINTS.ORDERS.UPDATE_STATUS,
        {
          orderId: updateData.orderId,
          status: updateData.status,
          description: updateData.description,
          reason: updateData.reason,
          updatedBy: updateData.updatedBy
        }
      );
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Update order status error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update status',
        statusCode: error.response?.status || 500,
      };
    }
  },

  /**
   * Grant access to digital product
   * POST /Orders/{orderId}/grant-access
   */
  grantAccess: async (orderId, accessData) => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.ORDERS.GRANT_ACCESS(orderId),
        accessData
      );
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Grant access error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to grant access',
        statusCode: error.response?.status || 500,
      };
    }
  },

  /**
   * Whether the order's GitHub username is a collaborator on each purchased repository.
   * GET /Github/verify-order-access/{orderId}  (orderId = order number, e.g. "PK-…")
   * `success` here only means the request worked. The check itself is in
   * data.data: { success, message?, accessDetails: [{ productName, repositoryName, githubUsername, hasAccess } | { productName, status }] }
   */
  verifyAccess: async (orderId) => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.GITHUB.VERIFY_ORDER_ACCESS(orderId)
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error, "We couldn't check repository access right now."),
        statusCode: error.response?.status || 500,
      };
    }
  },

  /**
   * Get deliverables for an order
   * GET /api/Deliverable/order/{orderId}
   */
  getDeliverablesByOrder: async (orderId) => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.DELIVERABLES.GET_BY_ORDER(orderId)
      );
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Get deliverables error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get deliverables',
      };
    }
  },

  /**
   * Manual attach deliverable
   * POST /api/Deliverable/manual-attach
   */
  manualAttachDeliverables: async (payload) => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.DELIVERABLES.MANUAL_ATTACH,
        payload
      );
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Manual attach error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to attach deliverable',
        statusCode: error.response?.status || 500,
      };
    }
  },
};

export default orderService;
